/**
 * web/ Framework7 라우트 셸 (Issue #447 Phase A → Phase G → #455 Phase H)
 *
 * Phase H 변경:
 *   - `/`, `/feed` → `@app/pages/FeedPage` alias 재사용
 *   - `/detail/:id` → DetailPageRoute wrapper (D3)
 *   - `/settings` 신규 → `@app/pages/SettingsPage` alias 재사용
 *   - `/login`/`/auth/callback` → 기존 `WebLoginPage`/`AuthCallbackPage` 유지 (D9)
 *
 * 라우트 분류:
 *   public:    /share/:id, /privacy, /terms
 *   auth-only: /login, /auth/callback (인증되어 있으면 / 로 skip)
 *   protected: /, /feed, /detail/:id, /settings (미인증이면 /login 으로 redirect)
 */

import type { Router } from 'framework7/types';
import PlaceholderPage from './pages/PlaceholderPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import { requireAuth, skipIfAuthed } from './lib/auth-guard';

// Issue #456 — F7 v9 asyncComponent로 라우트 단위 lazy 분할.
// `() => import(...)` 단일 형태 사용 (래퍼 금지 — Vite static analysis 보호).
// `/`와 `/feed`는 동일 모듈 참조하도록 공유 factory 사용 → Rollup이 단일 청크로 dedup.
const loadFeedPage = () => import('@app/pages/FeedPage');

const routes: Router.RouteParameters[] = [
  // 보호 라우트 — lazy (Issue #456)
  { path: '/', asyncComponent: loadFeedPage, beforeEnter: requireAuth },
  { path: '/feed', asyncComponent: loadFeedPage, beforeEnter: requireAuth },
  {
    path: '/detail/:id',
    asyncComponent: () => import('./pages/DetailPageRoute'),
    beforeEnter: requireAuth,
  },
  {
    path: '/settings',
    asyncComponent: () => import('@app/pages/SettingsPage'),
    beforeEnter: requireAuth,
  },

  // 공개 — share OG 페이지는 비로그인 접근 가능 (PlaceholderPage 공유, eager 유지)
  {
    path: '/share/:id',
    component: PlaceholderPage,
    options: { props: { label: 'Share (별도 이슈)' } },
  },

  // 인증 진입점 — lazy
  {
    path: '/login',
    asyncComponent: () => import('./pages/WebLoginPage'),
    beforeEnter: skipIfAuthed,
  },
  { path: '/auth/callback', asyncComponent: () => import('./pages/AuthCallbackPage') },

  // 정적 법적 문서 — eager (가벼움; Phase 0 결과로 후속 lazy 검토 가능)
  { path: '/privacy', component: PrivacyPage },
  { path: '/terms', component: TermsPage },

  // 404 — eager (PlaceholderPage 공유, 별도 청크 가치 없음)
  {
    path: '(.*)',
    component: PlaceholderPage,
    options: { props: { label: 'Not Found' } },
  },
];

export default routes;
